import React, { useState, useEffect } from "react";
import { formatScholarId } from "../utils/idFormatter";
import { getScholarScheduleDetails } from "../services/adminService";
import {
  X,
  FileText,
  Calendar,
  CalendarCheck,
  CreditCard,
  Star,
  LifeBuoy,
  Mail,
  Phone,
  Building2,
  Landmark,
  ShieldCheck,
  ZoomIn,
  ExternalLink,
  Copy,
  Check,
  AlertTriangle,
  Clock,
  Award,
  Sun,
  Moon,
  Sunrise,
  Layers,
  MapPin,
  Navigation,
  TrendingUp,
  CheckCircle2,
} from "lucide-react";

// ── Helpers ──────────────────────────────────────────────────
const fmt = (d: string | undefined) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtTime = (d: string | undefined) =>
  d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const inr = (n: number | undefined) => `₹${(n || 0).toLocaleString("en-IN")}`;
const maskAcc = (acc?: string) => acc ? `•••• ${String(acc).slice(-4)}` : "—";
const copyText = (text: string) => { try { navigator.clipboard.writeText(text); } catch (_) {} };

const statusColor: Record<string, { bg: string; fg: string }> = {
  COMPLETED: { bg: "#dcfce7", fg: "#15803d" },
  SUCCESS: { bg: "#dcfce7", fg: "#15803d" },
  APPROVED: { bg: "#dcfce7", fg: "#15803d" },
  PROCESSED: { bg: "#dcfce7", fg: "#15803d" },
  RESOLVED: { bg: "#dcfce7", fg: "#15803d" },
  CLOSED: { bg: "#e0e7ff", fg: "#4338ca" },
  PENDING: { bg: "#fff7ed", fg: "#c2410c" },
  PENDING_REVIEW: { bg: "#fff7ed", fg: "#c2410c" },
  IN_PROGRESS: { bg: "#eff6ff", fg: "#1d4ed8" },
  OPEN: { bg: "#eff6ff", fg: "#1d4ed8" },
  ACCEPTED: { bg: "#eff6ff", fg: "#1d4ed8" },
  CANCELLED: { bg: "#fee2e2", fg: "#b91c1c" },
  FAILED: { bg: "#fee2e2", fg: "#b91c1c" },
  REJECTED: { bg: "#fee2e2", fg: "#b91c1c" },
  DISPUTED: { bg: "#fef3c7", fg: "#92400e" },
  REFUNDED: { bg: "#fae8ff", fg: "#86198f" },
  SUSPENDED: { bg: "#fee2e2", fg: "#b91c1c" },
  WAIVED: { bg: "#dcfce7", fg: "#15803d" },
  ACTIVE: { bg: "#dcfce7", fg: "#15803d" },
};

const Badge = ({ status }: { status: string }) => {
  const c = statusColor[status] || { bg: "#f1f5f9", fg: "#475569" };
  return (
    <span style={{ padding: "3px 10px", borderRadius: 20, background: c.bg, color: c.fg, fontSize: 11, fontWeight: 700, display: "inline-block" }}>
      {status.replace(/_/g, " ")}
    </span>
  );
};

const StarRating = ({ rating }: { rating: number }) => (
  <div style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        size={14}
        fill={s <= Math.round(rating) ? "#f59e0b" : "none"}
        color={s <= Math.round(rating) ? "#f59e0b" : "#cbd5e1"}
      />
    ))}
    <span style={{ color: "#94a3b8", fontSize: 12, marginLeft: 4, fontWeight: 700 }}>{rating.toFixed(1)}</span>
  </div>
);

// ── Styles ──────────────────────────────────────────────────
const overlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 11000,
  background: "rgba(15,23,42,0.55)",
  backdropFilter: "blur(6px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const modal: React.CSSProperties = {
  background: "#fff",
  borderRadius: 20,
  width: "min(95vw, 980px)",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 25px 60px rgba(0,0,0,0.18)",
  animation: "profileSlideUp .25s ease",
  overflow: "hidden",
};

const tabBar: React.CSSProperties = {
  display: "flex",
  gap: 4,
  borderBottom: "1px solid #e2e8f0",
  padding: "0 28px",
  flexShrink: 0,
  overflowX: "auto",
  background: "#f8fafc",
};

const tabBtn = (active: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "14px 18px",
  fontSize: 13,
  fontWeight: active ? 700 : 600,
  color: active ? "#2563eb" : "#64748b",
  cursor: "pointer",
  borderBottom: active ? "2.5px solid #2563eb" : "2.5px solid transparent",
  background: "none",
  border: "none",
  whiteSpace: "nowrap",
  transition: "all .15s",
});

const statCard: React.CSSProperties = {
  background: "#f8fafc",
  borderRadius: 14,
  padding: "16px 20px",
  border: "1px solid #e2e8f0",
  flex: 1,
  minWidth: 140,
};

const scrollBody: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: "24px 28px",
};

const tbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 12px",
  color: "#94a3b8",
  fontWeight: 700,
  fontSize: 11,
  textTransform: "uppercase",
  borderBottom: "1px solid #e2e8f0",
};
const td: React.CSSProperties = {
  padding: "12px 12px",
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
};

// Info card row
const InfoPair = ({ label, value, mono }: { label: string; value?: string | number | null; mono?: boolean }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
    <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>{label}</span>
    <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 600, fontFamily: mono ? "monospace" : undefined, textAlign: "right", maxWidth: "60%", wordBreak: "break-all" }}>
      {value ?? "—"}
    </span>
  </div>
);

// Section card with colored left border
const InfoCard = ({ title, icon: Icon, accent, children }: {
  title: string; icon: React.ElementType; accent: string; children: React.ReactNode;
}) => (
  <div style={{
    borderRadius: 14,
    border: "1px solid #e2e8f0",
    borderLeft: `4px solid ${accent}`,
    overflow: "hidden",
    marginBottom: 20,
    background: "#fff",
  }}>
    <div style={{
      padding: "12px 18px",
      background: "#f8fafc",
      borderBottom: "1px solid #f1f5f9",
      display: "flex",
      alignItems: "center",
      gap: 10,
    }}>
      <Icon size={16} color={accent} />
      <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{title}</span>
    </div>
    <div style={{ padding: "8px 18px 14px" }}>{children}</div>
  </div>
);

// ── Shared types ────────────────────────────────────────────
type Tab = "overview" | "bookings" | "transactions" | "reviews" | "tickets" | "documents" | "schedule";

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
  data: any;
  loading: boolean;
  type: "user" | "scholar";
  onRejected?: () => void;
}

// ── The Modal ───────────────────────────────────────────────
const ProfileModal: React.FC<ProfileModalProps> = ({ open, onClose, data, loading, type, onRejected }) => {
  const [tab, setTab] = useState<Tab>("overview");
  if (!open) return null;

  type TabDef = { key: Tab; label: string; icon: React.ElementType; scholarOnly?: boolean };
  const allTabs: TabDef[] = [
    { key: "overview", label: "Overview", icon: Layers },
    { key: "documents", label: "Documents", icon: FileText, scholarOnly: true },
    { key: "schedule", label: "Schedule", icon: Calendar, scholarOnly: true },
    { key: "bookings", label: "Bookings", icon: CalendarCheck },
    { key: "transactions", label: "Transactions", icon: CreditCard },
    { key: "reviews", label: "Reviews", icon: Star },
    { key: "tickets", label: "Support", icon: LifeBuoy },
  ];
  const tabs = allTabs.filter(t => !t.scholarOnly || type === "scholar");

  const name = type === "user" ? data?.name : data?.user?.name;
  const email = type === "user" ? data?.email : data?.user?.email;
  const phone = type === "user" ? data?.phone : data?.user?.phone;
  const religion = type === "user" ? data?.religion : data?.user?.religion || data?.religion;
  const gender = type === "user" ? data?.gender : data?.user?.gender;
  const joinDate = type === "user" ? data?.createdAt : data?.user?.createdAt;
  const lastActive = type === "user" ? data?.updatedAt : data?.user?.updatedAt;
  const isSuspended = type === "user" ? data?.isSuspended : data?.user?.isSuspended;
  const isVerified = type === "user" ? data?.isVerified : data?.user?.isVerified;

  const bookings = type === "user" ? (data?.customer?.bookings || []) : (data?.bookings || []);
  const reviews = type === "user" ? (data?.customer?.reviews || []) : (data?.reviews || []);
  const tickets = data?.supportTickets || [];
  const stats = data?.stats || {};

  const transactions = bookings
    .filter((b: any) => b.payment)
    .map((b: any) => ({
      ...b.payment,
      serviceName: b.service?.name,
      bookingStatus: b.status,
      scheduledAt: b.scheduledAt,
      scholarCharge: b.scholarCharge,
      platformCharge: b.platformCharge,
      counterparty: type === "user" ? b.scholar?.user?.name : b.customer?.user?.name,
    }));

  return (
    <>
      <style>{`
        @keyframes profileSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .profile-modal-body::-webkit-scrollbar { width: 6px; }
        .profile-modal-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
        .profile-row:hover { background: #f8fafc; }
      `}</style>
      <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div style={modal} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={{ padding: "20px 28px", borderBottom: "1px solid #e2e8f0", background: "#fff" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                <div style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #2563eb, #7c3aed)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 20,
                  fontWeight: 800,
                  boxShadow: "0 4px 14px rgba(37,99,235,0.25)",
                  flexShrink: 0,
                }}>
                  {loading ? "…" : (name || "?").charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
                    {loading ? "Loading profile…" : name || "Unknown"}
                  </h2>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
                    {!loading && (
                      <>
                        <span style={{ fontSize: 12, color: "#64748b", display: "flex", alignItems: "center", gap: 4 }}>
                          <Mail size={12} /> {email || "No email"}
                        </span>
                        <span style={{ fontSize: 10, color: "#cbd5e1" }}>•</span>
                        <span style={{ fontSize: 12, color: "#64748b", display: "flex", alignItems: "center", gap: 4 }}>
                          <Phone size={12} /> {phone || "No phone"}
                        </span>
                        {type === "scholar" && data?.status && <Badge status={data.status} />}
                        {isSuspended && <Badge status="SUSPENDED" />}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#64748b",
                  transition: "all .15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#fee2e2"; e.currentTarget.style.color = "#ef4444"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#64748b"; }}
                title="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div style={tabBar}>
            {tabs.map((t) => {
              const TabIcon = t.icon;
              return (
                <button key={t.key} style={tabBtn(tab === t.key)} onClick={() => setTab(t.key)}>
                  <TabIcon size={15} />
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Modal Body */}
          <div className="profile-modal-body" style={scrollBody}>
            {loading ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#94a3b8" }}>
                <Clock size={36} style={{ animation: "spin 2s linear infinite", marginBottom: 12 }} />
                <p style={{ fontSize: 14, fontWeight: 600 }}>Loading profile data…</p>
              </div>
            ) : (
              <>
                {tab === "overview" && (
                  <OverviewTab
                    type={type}
                    data={data}
                    name={name}
                    email={email}
                    phone={phone}
                    religion={religion}
                    gender={gender}
                    joinDate={joinDate}
                    lastActive={lastActive}
                    isVerified={isVerified}
                    stats={stats}
                  />
                )}

                {tab === "bookings" && (
                  <div>
                    {bookings.length === 0 ? (
                      <EmptyState icon={CalendarCheck} text="No bookings found for this profile." />
                    ) : (
                      <table style={tbl}>
                        <thead>
                          <tr>
                            <th style={th}>Service</th>
                            <th style={th}>{type === "user" ? "Scholar" : "Customer"}</th>
                            <th style={th}>Date & Time</th>
                            <th style={th}>Duration</th>
                            <th style={th}>Price</th>
                            <th style={th}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bookings.map((b: any) => (
                            <tr key={b.id} className="profile-row">
                              <td style={td}><b>{b.service?.name || "—"}</b></td>
                              <td style={td}>{type === "user" ? b.scholar?.user?.name : b.customer?.user?.name || "—"}</td>
                              <td style={td}>{fmtTime(b.scheduledAt)}</td>
                              <td style={td}>{b.durationMin} min</td>
                              <td style={td}><b>{inr(b.price)}</b></td>
                              <td style={td}><Badge status={b.status} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {tab === "transactions" && (
                  <TransactionsTab
                    type={type}
                    transactions={transactions}
                    payouts={data?.payouts || []}
                    penalties={data?.penalties || []}
                    stats={stats}
                  />
                )}

                {tab === "reviews" && (
                  <div>
                    {reviews.length === 0 ? (
                      <EmptyState icon={Star} text="No reviews yet." />
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        {reviews.map((r: any) => (
                          <div key={r.id} style={{ padding: 18, borderRadius: 14, border: "1px solid #e2e8f0", background: "#fafafa" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                              <div>
                                <span style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>
                                  {type === "user" ? r.scholar?.user?.name : r.customer?.user?.name || "Unknown"}
                                </span>
                                <span style={{ color: "#94a3b8", fontSize: 12, marginLeft: 10 }}>{fmt(r.createdAt)}</span>
                              </div>
                              <StarRating rating={r.rating} />
                            </div>
                            <p style={{ margin: 0, fontSize: 13, color: "#475569", lineHeight: 1.5 }}>
                              {r.comment || <i style={{ color: "#94a3b8" }}>No comment provided</i>}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {tab === "schedule" && type === "scholar" && <ScheduleTab data={data} />}

                {tab === "documents" && (
                  <DocumentsTab data={data} scholarId={data?.id} onRejected={onRejected} />
                )}

                {tab === "tickets" && (
                  <div>
                    {tickets.length === 0 ? (
                      <EmptyState icon={LifeBuoy} text="No support tickets found." />
                    ) : (
                      <table style={tbl}>
                        <thead>
                          <tr>
                            <th style={th}>Subject</th>
                            <th style={th}>Category</th>
                            <th style={th}>Priority</th>
                            <th style={th}>Status</th>
                            <th style={th}>Created</th>
                            <th style={th}>Resolved</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tickets.map((t: any) => (
                            <tr key={t.id} className="profile-row">
                              <td style={td}><b>{t.subject}</b></td>
                              <td style={td}>{t.category}</td>
                              <td style={td}>
                                <span style={{
                                  padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                                  background: t.priority === "HIGH" ? "#fee2e2" : t.priority === "MEDIUM" ? "#fff7ed" : "#f1f5f9",
                                  color: t.priority === "HIGH" ? "#b91c1c" : t.priority === "MEDIUM" ? "#c2410c" : "#64748b",
                                }}>
                                  {t.priority}
                                </span>
                              </td>
                              <td style={td}><Badge status={t.status} /></td>
                              <td style={td}>{fmt(t.createdAt)}</td>
                              <td style={td}>{t.resolvedAt ? fmt(t.resolvedAt) : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// ── Overview Tab ─────────────────────────────────────────────
const OverviewTab = ({ type, data, name, email, phone, religion, gender, joinDate, lastActive, isVerified, stats }: any) => {
  return (
    <div>
      {/* Top Stat Metrics */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 28 }}>
        <div style={statCard}>
          <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Total Bookings</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#1e293b", marginTop: 4 }}>{stats.totalBookings ?? 0}</div>
          <div style={{ fontSize: 12, color: "#10b981", fontWeight: 600, marginTop: 2 }}>{stats.completedBookings ?? 0} completed</div>
        </div>
        <div style={statCard}>
          <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>
            {type === "user" ? "Total Spent" : "Scholar Earnings"}
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#2563eb", marginTop: 4 }}>
            {inr(type === "user" ? stats.totalSpent : stats.totalScholarEarnings)}
          </div>
          {type === "scholar" && <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Gross: {inr(stats.totalEarnings)}</div>}
        </div>
        {type === "scholar" && (
          <>
            <div style={statCard}>
              <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Redeemed (Paid Out)</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#10b981", marginTop: 4 }}>{inr(stats.totalRedeemed)}</div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{stats.totalPayouts ?? 0} payouts</div>
            </div>
            <div style={{ ...statCard, border: "1px solid #bfdbfe", background: "#eff6ff" }}>
              <div style={{ fontSize: 11, color: "#1d4ed8", fontWeight: 700, textTransform: "uppercase" }}>Available Balance</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#1d4ed8", marginTop: 4 }}>{inr(stats.pendingBalance)}</div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Pending release</div>
            </div>
          </>
        )}
        <div style={statCard}>
          <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Reviews</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#f59e0b", marginTop: 4 }}>{stats.totalReviews ?? 0}</div>
          {type === "scholar" && stats.avgRating > 0 && (
            <div style={{ marginTop: 2 }}>
              <StarRating rating={stats.avgRating} />
            </div>
          )}
        </div>
      </div>

      {/* Personal Info Grid */}
      <h4 style={{ fontSize: 13, fontWeight: 800, color: "#475569", marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
        Personal Information
      </h4>
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 32px",
        background: "#f8fafc", padding: 20, borderRadius: 14, border: "1px solid #e2e8f0", marginBottom: 24,
      }}>
        {[
          { label: "Full Name", value: name },
          { label: "Email", value: email || "—" },
          { label: "Phone", value: phone || "—" },
          { label: "Religion", value: religion || "—" },
          { label: "Gender", value: gender || "—" },
          { label: "Verified", value: isVerified ? "✓ Yes" : "✗ No" },
          { label: "Date Joined", value: fmt(joinDate) },
          { label: "Last Active", value: fmt(lastActive) },
          ...(type === "user" && data?.customer
            ? [{ label: "City", value: data.customer.city || "—" }, { label: "State", value: data.customer.state || "—" }]
            : []),
          ...(type === "scholar"
            ? [
                { label: "Scholar ID", value: formatScholarId(data?.id || data?.scholarId) },
                { label: "Scholar Status", value: data?.status || "—" },
                { label: "Scholar Type", value: data?.scholarType || "—" },
                { label: "Specialization", value: data?.specialization || "—" },
                { label: "Experience", value: data?.yearsOfExperience || data?.experience || "—" },
                { label: "Languages", value: (data?.languages || []).join(", ") || "—" },
                { label: "Community", value: data?.community || "—" },
                { label: "City", value: data?.city || "—" },
                { label: "State", value: data?.state || "—" },
                { label: "Available", value: data?.isAvailable ? "✓ Yes" : "✗ No" },
                { label: "Onboarding Step", value: data?.onboardingStep || "—" },
              ]
            : []),
        ].map((item, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #edf2f7" }}>
            <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>{item.label}</span>
            <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 600 }}>{item.value}</span>
          </div>
        ))}
      </div>

      {/* Scholar: Bank & Payout Card */}
      {type === "scholar" && (() => {
        const pd = data?.paymentDetails as Record<string, any> | null;
        if (!pd) return null;
        return (
          <InfoCard title="Payout & Bank Account Details" icon={Landmark} accent="#2563eb">
            <InfoPair label="Account Holder" value={pd.accountHolder || pd.accountHolderName} />
            <InfoPair label="Bank Name" value={pd.bankName} />
            <InfoPair label="Branch" value={pd.branch} />
            <InfoPair label="Account Type" value={pd.accountType} />
            <InfoPair label="Account Number" value={maskAcc(pd.accountNumber)} mono />
            <InfoPair label="IFSC Code" value={pd.ifscCode || pd.routingCode} mono />
            {pd.upiId && <InfoPair label="UPI ID" value={pd.upiId} mono />}
            {pd.termsAccepted !== undefined && <InfoPair label="Terms Accepted" value={pd.termsAccepted ? "✓ Yes" : "✗ No"} />}
            <div style={{ display: "flex", gap: 16, marginTop: 14, flexWrap: "wrap", padding: "12px 0", borderTop: "1px solid #f1f5f9" }}>
              {[
                { label: "Total Paid Out", value: inr(stats.totalRedeemed), color: "#10b981" },
                { label: "Available Balance", value: inr(stats.pendingBalance), color: "#2563eb" },
                { label: "Payouts Count", value: String(stats.totalPayouts ?? 0), color: "#64748b" },
              ].map((s, i) => (
                <div key={i} style={{ flex: "1 1 120px", textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>{s.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: s.color, marginTop: 2 }}>{s.value}</div>
                </div>
              ))}
            </div>
          </InfoCard>
        );
      })()}

      {/* Scholar: Religious Institution & Affiliation Card */}
      {type === "scholar" && (() => {
        const rc = data?.religiousCertification as Record<string, any> | null;
        if (!rc) return null;
        return (
          <InfoCard title="Religious Institution & Affiliation" icon={Building2} accent="#8b5cf6">
            {rc.institutionName && <InfoPair label="Institution Name" value={rc.institutionName || rc.institution} />}
            {rc.referenceType && <InfoPair label="Role / Position" value={rc.referenceType || rc.degree} />}
            {rc.referencePerson && <InfoPair label="Reference Person" value={rc.referencePerson} />}
            {rc.referencePhone && <InfoPair label="Reference Phone" value={rc.referencePhone} />}
            {rc.referenceEmail && <InfoPair label="Reference Email" value={rc.referenceEmail} />}
            {Array.isArray(rc.certificates) && rc.certificates.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}>
                  Certificates ({rc.certificates.length})
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {rc.certificates.map((cert: any, idx: number) => (
                    <div key={cert.id || idx} style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "10px 14px", borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0",
                    }}>
                      <Award size={18} color="#8b5cf6" />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: "#1e293b" }}>{cert.name || `Certificate ${idx + 1}`}</div>
                        {cert.issueDate && (
                          <div style={{ fontSize: 11, color: "#94a3b8" }}>
                            Issued: {cert.issueDate}{cert.expiryDate ? ` • Expires: ${cert.expiryDate}` : ""}
                          </div>
                        )}
                      </div>
                      {cert.file && (
                        <a href={cert.file} target="_blank" rel="noreferrer"
                          style={{ fontSize: 11, fontWeight: 700, color: "#2563eb", textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>
                          View <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {rc.referenceLetter && (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, marginBottom: 6, textTransform: "uppercase" }}>Reference Letter</div>
                <a href={rc.referenceLetter} target="_blank" rel="noreferrer"
                  style={{ fontSize: 13, fontWeight: 700, color: "#2563eb", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <FileText size={15} /> View Reference Letter <ExternalLink size={13} />
                </a>
              </div>
            )}
            {!rc.institutionName && !rc.referencePerson && !(rc.certificates?.length) && (
              <div style={{ color: "#94a3b8", fontStyle: "italic", fontSize: 13, padding: "8px 0" }}>No affiliation details provided.</div>
            )}
          </InfoCard>
        );
      })()}

      {/* Services Offered */}
      {type === "scholar" && data?.services?.length > 0 && (
        <>
          <h4 style={{ fontSize: 13, fontWeight: 800, color: "#475569", marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>Services Offered</h4>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
            {data.services.map((s: any) => (
              <div key={s.id} style={{ padding: "10px 16px", borderRadius: 12, border: "1px solid #e2e8f0", background: s.isActive ? "#f0fdf4" : "#fef2f2", fontSize: 13 }}>
                <div style={{ fontWeight: 700, color: "#1e293b" }}>{s.service?.name}</div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{inr(s.price)} • {s.durationMin} min</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Recent Payouts */}
      {type === "scholar" && data?.payouts?.length > 0 && (
        <>
          <h4 style={{ fontSize: 13, fontWeight: 800, color: "#475569", marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>Recent Payouts</h4>
          <table style={tbl}>
            <thead><tr><th style={th}>Amount</th><th style={th}>Type</th><th style={th}>Status</th><th style={th}>Date</th><th style={th}>Processed</th></tr></thead>
            <tbody>
              {data.payouts.slice(0, 5).map((p: any) => (
                <tr key={p.id} className="profile-row">
                  <td style={td}><b>{inr(p.amount)}</b></td>
                  <td style={td}><span style={{ fontSize: 11, color: "#64748b" }}>{p.type || "EARNINGS"}</span></td>
                  <td style={td}><Badge status={p.status} /></td>
                  <td style={td}>{fmt(p.createdAt)}</td>
                  <td style={td}>{p.processedAt ? fmt(p.processedAt) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

// ── Transactions Tab ─────────────────────────────────────────
type TxLedgerFilter = "all" | "earnings" | "payouts" | "penalties";

const TransactionsTab = ({ type, transactions, payouts, penalties, stats }: {
  type: string; transactions: any[]; payouts: any[]; penalties: any[]; stats: any;
}) => {
  const [filter, setFilter] = useState<TxLedgerFilter>("all");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = (text: string) => {
    copyText(text);
    setCopiedId(text);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filterBtn = (key: TxLedgerFilter, label: string, count: number, color: string) => (
    <button
      onClick={() => setFilter(key)}
      style={{
        padding: "7px 16px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 700,
        cursor: "pointer",
        border: filter === key ? `2px solid ${color}` : "2px solid #e2e8f0",
        background: filter === key ? color : "#fff",
        color: filter === key ? "#fff" : "#64748b",
        transition: "all .15s",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {label}
      <span style={{
        fontSize: 10,
        fontWeight: 800,
        background: filter === key ? "rgba(255,255,255,0.3)" : "#f1f5f9",
        color: filter === key ? "#fff" : "#475569",
        padding: "1px 7px",
        borderRadius: 10,
      }}>
        {count}
      </span>
    </button>
  );

  const earningsRows = transactions.filter((t) => t.status === "SUCCESS").map((t) => ({
    _type: "earning" as const,
    id: t.id,
    label: t.serviceName || "Service Payment",
    counterparty: t.counterparty,
    gross: t.amount,
    scholarShare: t.scholarCharge,
    platformFee: t.platformCharge,
    status: t.status,
    date: t.createdAt,
    ref: t.razorpayPaymentId || t.razorpayOrderId,
  }));

  const payoutRows = payouts.map((p: any) => ({
    _type: "payout" as const,
    id: p.id,
    label: p.type === "BONUS" ? "Bonus Payment" : p.type === "PENALTY_REFUND" ? "Penalty Refund" : "Payout / Redeem",
    note: p.note,
    amount: p.amount,
    processingFee: p.processingFee,
    originalAmount: p.originalAmount,
    status: p.status,
    date: p.createdAt,
    processedAt: p.processedAt,
    ref: p.razorpayPayoutId,
  }));

  const penaltyRows = penalties.map((pen: any) => ({
    _type: "penalty" as const,
    id: pen.id,
    label: "Penalty / Deduction",
    reason: pen.reason,
    amount: pen.amount,
    status: pen.waivedByAdmin ? "WAIVED" : (pen.status || "ACTIVE"),
    waivedReason: pen.waivedReason,
    date: pen.createdAt,
  }));

  const allRows = [
    ...earningsRows.map(r => ({ ...r, _sortDate: r.date })),
    ...payoutRows.map(r => ({ ...r, _sortDate: r.date })),
    ...penaltyRows.map(r => ({ ...r, _sortDate: r.date })),
  ].sort((a, b) => new Date(b._sortDate).getTime() - new Date(a._sortDate).getTime());

  const visibleRows: any[] = filter === "all" ? allRows
    : filter === "earnings" ? earningsRows
    : filter === "payouts" ? payoutRows
    : penaltyRows;

  if (type !== "scholar") {
    return (
      <div>
        {transactions.length === 0 ? (
          <EmptyState icon={CreditCard} text="No transactions found." />
        ) : (
          <table style={tbl}>
            <thead>
              <tr>
                <th style={th}>Service</th>
                <th style={th}>Scholar</th>
                <th style={th}>Amount</th>
                <th style={th}>Payment Status</th>
                <th style={th}>Booking Status</th>
                <th style={th}>Date</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t: any) => (
                <tr key={t.id} className="profile-row">
                  <td style={td}><b>{t.serviceName || "—"}</b></td>
                  <td style={td}>{t.counterparty || "—"}</td>
                  <td style={td}><b>{inr(t.amount)}</b></td>
                  <td style={td}><Badge status={t.status} /></td>
                  <td style={td}><Badge status={t.bookingStatus} /></td>
                  <td style={td}>{fmtTime(t.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Ledger Stats Summary Cards */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        {[
          { label: "Total Gross Earnings", value: inr(stats.totalEarnings), sub: "All completed bookings", color: "#1e293b", bg: "#f8fafc" },
          { label: "Scholar Share", value: inr(stats.totalScholarEarnings), sub: "After platform fee", color: "#2563eb", bg: "#eff6ff" },
          { label: "Total Redeemed", value: inr(stats.totalRedeemed), sub: `${stats.totalPayouts ?? 0} payouts`, color: "#10b981", bg: "#f0fdf4" },
          { label: "Available Balance", value: inr(stats.pendingBalance), sub: "Pending release", color: "#8b5cf6", bg: "#f5f3ff" },
        ].map((s, i) => (
          <div key={i} style={{ flex: "1 1 160px", padding: "16px 18px", borderRadius: 14, background: s.bg, border: `1px solid ${s.color}22` }}>
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color, margin: "4px 0" }}>{s.value}</div>
            <div style={{ fontSize: 11, color: "#64748b" }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Filter Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {filterBtn("all", "All Entries", allRows.length, "#0f172a")}
        {filterBtn("earnings", "Earnings", earningsRows.length, "#2563eb")}
        {filterBtn("payouts", "Payouts", payoutRows.length, "#10b981")}
        {filterBtn("penalties", "Penalties", penaltyRows.length, "#ef4444")}
      </div>

      {/* Ledger Table */}
      {visibleRows.length === 0 ? (
        <EmptyState icon={CreditCard} text="No transactions match the selected filter." />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={tbl}>
            <thead>
              <tr>
                <th style={th}>Type</th>
                <th style={th}>Details / Description</th>
                <th style={th}>Gross / Request</th>
                <th style={th}>Scholar Net</th>
                <th style={th}>Reference / ID</th>
                <th style={th}>Status</th>
                <th style={th}>Date</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row: any) => {
                const isEarning = row._type === "earning";
                const isPayout = row._type === "payout";
                const isPenalty = row._type === "penalty";

                return (
                  <tr key={`${row._type}-${row.id}`} className="profile-row">
                    {/* Type Badge */}
                    <td style={td}>
                      <span style={{
                        padding: "3px 9px",
                        borderRadius: 20,
                        fontSize: 11,
                        fontWeight: 700,
                        background: isEarning ? "#eff6ff" : isPayout ? "#f0fdf4" : "#fef2f2",
                        color: isEarning ? "#1d4ed8" : isPayout ? "#15803d" : "#b91c1c",
                        display: "inline-block",
                      }}>
                        {isEarning ? "Earning" : isPayout ? "Payout" : "Penalty"}
                      </span>
                    </td>

                    {/* Details */}
                    <td style={td}>
                      <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 13 }}>{row.label}</div>
                      {isEarning && row.counterparty && (
                        <div style={{ fontSize: 11, color: "#64748b" }}>From: {row.counterparty}</div>
                      )}
                      {isPayout && row.note && (
                        <div style={{ fontSize: 11, color: "#64748b" }}>{row.note}</div>
                      )}
                      {isPenalty && (
                        <div style={{ fontSize: 11, color: "#dc2626" }}>
                          {row.reason}
                          {row.waivedReason && <span style={{ color: "#16a34a", marginLeft: 4 }}>• Waived: {row.waivedReason}</span>}
                        </div>
                      )}
                    </td>

                    {/* Gross */}
                    <td style={td}>
                      {isEarning && <span>{inr(row.gross)}</span>}
                      {isPayout && <span>{inr(row.originalAmount || row.amount)}</span>}
                      {isPenalty && <span style={{ color: "#ef4444" }}>—</span>}
                    </td>

                    {/* Net Amount */}
                    <td style={td}>
                      {isEarning && (
                        <div>
                          <span style={{ fontWeight: 800, color: "#15803d" }}>+{inr(row.scholarShare || row.gross)}</span>
                          {row.platformFee ? (
                            <div style={{ fontSize: 10, color: "#94a3b8" }}>Fee: {inr(row.platformFee)}</div>
                          ) : null}
                        </div>
                      )}
                      {isPayout && (
                        <div>
                          <span style={{ fontWeight: 800, color: "#2563eb" }}>-{inr(row.amount)}</span>
                          {row.processingFee ? (
                            <div style={{ fontSize: 10, color: "#94a3b8" }}>Fee: {inr(row.processingFee)}</div>
                          ) : null}
                        </div>
                      )}
                      {isPenalty && (
                        <span style={{ fontWeight: 800, color: row.status === "WAIVED" ? "#94a3b8" : "#dc2626", textDecoration: row.status === "WAIVED" ? "line-through" : "none" }}>
                          -{inr(row.amount)}
                        </span>
                      )}
                    </td>

                    {/* Ref / Copy */}
                    <td style={td}>
                      {row.ref ? (
                        <button
                          onClick={() => handleCopy(row.ref)}
                          style={{
                            background: "#f1f5f9",
                            border: "1px solid #e2e8f0",
                            borderRadius: 6,
                            padding: "3px 8px",
                            fontSize: 11,
                            fontFamily: "monospace",
                            color: "#475569",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                          title="Click to copy ID"
                        >
                          {String(row.ref).slice(0, 10)}…
                          {copiedId === row.ref ? <Check size={11} color="#16a34a" /> : <Copy size={11} />}
                        </button>
                      ) : (
                        <span style={{ color: "#cbd5e1" }}>—</span>
                      )}
                    </td>

                    {/* Status */}
                    <td style={td}>
                      <Badge status={row.status} />
                    </td>

                    {/* Date */}
                    <td style={td}>
                      <div style={{ fontSize: 12, color: "#475569" }}>{fmt(row.date)}</div>
                      <div style={{ fontSize: 10, color: "#94a3b8" }}>{fmtTime(row.date).split(",")[1]?.trim() || ""}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Documents Tab ───────────────────────────────────────────
const DocumentsTab = ({ data, scholarId, onRejected }: { data: any; scholarId?: string; onRejected?: () => void }) => {
  const [lightbox, setLightbox] = React.useState<string | null>(null);
  const [rejectModal, setRejectModal] = React.useState<{ docType: 'identity' | 'certification'; label: string } | null>(null);
  const [rejectReason, setRejectReason] = React.useState('');
  const [rejectLoading, setRejectLoading] = React.useState(false);
  const [rejectToast, setRejectToast] = React.useState<{ msg: string; ok: boolean } | null>(null);

  const idVerif = data?.identityVerification || {};
  const relCert = data?.religiousCertification || {};

  const showRejectToast = (msg: string, ok: boolean) => {
    setRejectToast({ msg, ok });
    setTimeout(() => setRejectToast(null), 3500);
  };

  const handleRejectSubmit = async () => {
    if (!rejectModal || !scholarId) return;
    if (!rejectReason.trim()) { showRejectToast('Please enter a rejection reason.', false); return; }
    setRejectLoading(true);
    try {
      const { rejectScholarDocument } = await import('../services/adminService');
      await rejectScholarDocument(scholarId, rejectModal.docType, rejectReason.trim());
      showRejectToast(`${rejectModal.label} rejected. Scholar will be asked to re-upload.`, true);
      setRejectModal(null);
      setRejectReason('');
      onRejected?.();
    } catch (e: any) {
      showRejectToast(e?.message || 'Failed to reject document.', false);
    } finally {
      setRejectLoading(false);
    }
  };

  const DocCard = ({ label, url, sublabel }: { label: string; url?: string; sublabel?: string }) => (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden", background: "#fff", flex: "1 1 220px", minWidth: 200, maxWidth: 280, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
      <div style={{ background: "#f8fafc", padding: "10px 14px", borderBottom: "1px solid #f1f5f9", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </div>
      {url ? (
        <div style={{ cursor: "zoom-in", position: "relative" }} onClick={() => setLightbox(url)} title="Click to enlarge">
          <img src={url} alt={label}
            style={{ width: "100%", height: 180, objectFit: "cover", display: "block", transition: "opacity .2s" }}
            onError={(e) => { (e.target as HTMLImageElement).src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f1f5f9' width='100' height='100'/%3E%3Ctext fill='%2394a3b8' font-size='12' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3EImage error%3C/text%3E%3C/svg%3E"; }}
          />
          <div style={{ position: "absolute", bottom: 8, right: 8, background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 11, padding: "4px 8px", borderRadius: 8, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
            <ZoomIn size={12} /> Click to zoom
          </div>
        </div>
      ) : (
        <div style={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 13, background: "#f8fafc" }}>
          No document uploaded
        </div>
      )}
      {sublabel && <div style={{ padding: "8px 14px", fontSize: 12, color: "#64748b", borderTop: "1px solid #f1f5f9" }}>{sublabel}</div>}
      {url && (
        <div style={{ padding: "8px 14px", borderTop: "1px solid #f1f5f9" }}>
          <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#2563eb", fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
            Open in new tab <ExternalLink size={12} />
          </a>
        </div>
      )}
    </div>
  );

  const hasIdDocs = Boolean(idVerif.documentImage || idVerif.selfieImage);
  const hasCertDoc = Boolean(relCert.certificateImage || relCert.letterImage);

  return (
    <div>
      {rejectToast && (
        <div style={{ position: "fixed", top: 16, right: 16, zIndex: 999999, padding: "12px 18px", borderRadius: 12, maxWidth: 340, background: rejectToast.ok ? "#f0fdf4" : "#fef2f2", border: `1px solid ${rejectToast.ok ? "#bbf7d0" : "#fecaca"}`, color: rejectToast.ok ? "#166534" : "#b91c1c", fontSize: 13, fontWeight: 600, boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}>
          {rejectToast.msg}
        </div>
      )}
      {rejectModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 99998, background: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={(e) => { if (e.target === e.currentTarget) setRejectModal(null); }}>
          <div style={{ background: "#fff", borderRadius: 20, padding: 28, width: "min(90vw,460px)", boxShadow: "0 25px 60px rgba(0,0,0,0.2)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <AlertTriangle size={22} color="#dc2626" />
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#0f172a" }}>Reject {rejectModal.label}</h3>
            </div>
            <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>The scholar will be notified and sent back to re-upload <b>{rejectModal.label}</b> only. Other onboarding steps will be preserved.</p>
            <textarea placeholder="Reason for rejection (e.g. blurry image, expired ID, name mismatch)..." value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={4}
              style={{ width: "100%", borderRadius: 12, border: "1.5px solid #e2e8f0", padding: "12px 14px", fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
              <button onClick={() => { setRejectModal(null); setRejectReason(''); }} style={{ padding: "10px 18px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={handleRejectSubmit} disabled={rejectLoading} style={{ padding: "10px 22px", borderRadius: 10, border: "none", background: rejectLoading ? "#fca5a5" : "#ef4444", color: "#fff", fontSize: 13, fontWeight: 700, cursor: rejectLoading ? "not-allowed" : "pointer" }}>
                {rejectLoading ? "Rejecting…" : "Reject Document"}
              </button>
            </div>
          </div>
        </div>
      )}
      {lightbox && (
        <div style={{ position: "fixed", inset: 0, zIndex: 99999, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Document preview" style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 12, boxShadow: "0 25px 80px rgba(0,0,0,0.6)" }} />
          <button style={{ position: "absolute", top: 20, right: 24, background: "none", border: "none", color: "#fff", cursor: "pointer" }} onClick={() => setLightbox(null)}>
            <X size={32} />
          </button>
        </div>
      )}

      {/* Identity Verification Section */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ShieldCheck size={18} color="#2563eb" />
          <h4 style={{ fontSize: 13, fontWeight: 800, color: "#475569", margin: 0, textTransform: "uppercase", letterSpacing: 0.5 }}>Identity Verification</h4>
        </div>
        {hasIdDocs && scholarId && (
          <button onClick={() => setRejectModal({ docType: 'identity', label: 'Identity Documents' })} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #fecaca", background: "#fff", color: "#dc2626", fontWeight: 700, fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <AlertTriangle size={14} /> Reject Identity Docs
          </button>
        )}
      </div>
      <div style={{ background: "#f8fafc", borderRadius: 12, padding: "14px 18px", marginBottom: 16, border: "1px solid #e2e8f0", fontSize: 13 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 32px" }}>
          {[{ label: "ID Type", value: idVerif.idType || "—" }, { label: "ID Number", value: idVerif.idNumber || "—" }, { label: "Status", value: idVerif.verificationStatus || "—" }].map((r, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
              <span style={{ color: "#94a3b8", fontWeight: 500 }}>{r.label}</span>
              <span style={{ color: "#1e293b", fontWeight: 600 }}>{r.value}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 32 }}>
        <DocCard label="ID Document (Front)" url={idVerif.documentImage} sublabel="Government-issued photo ID" />
        <DocCard label="Selfie" url={idVerif.selfieImage} sublabel="Live face photo for verification" />
      </div>

      {/* Religious Certification Section */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Building2 size={18} color="#8b5cf6" />
          <h4 style={{ fontSize: 13, fontWeight: 800, color: "#475569", margin: 0, textTransform: "uppercase", letterSpacing: 0.5 }}>Religious Certification</h4>
        </div>
        {hasCertDoc && scholarId && (
          <button onClick={() => setRejectModal({ docType: 'certification', label: 'Religious Certification' })} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #fecaca", background: "#fff", color: "#dc2626", fontWeight: 700, fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <AlertTriangle size={14} /> Reject Certification
          </button>
        )}
      </div>
      {hasCertDoc ? (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {relCert.certificateImage && <DocCard label="Certificate" url={relCert.certificateImage} sublabel={relCert.certBody || relCert.certificationBody || ""} />}
          {relCert.letterImage && <DocCard label="Reference Letter" url={relCert.letterImage} />}
        </div>
      ) : (
        !hasIdDocs
          ? <EmptyState icon={FileText} text="No documents have been uploaded by this scholar yet." />
          : <div style={{ color: "#94a3b8", fontSize: 13, padding: "20px 0" }}>No religious certification uploaded yet.</div>
      )}
    </div>
  );
};

const EmptyState = ({ icon: Icon, text }: { icon: React.ElementType; text: string }) => (
  <div style={{ textAlign: "center", padding: "50px 20px", color: "#94a3b8" }}>
    <Icon size={36} color="#cbd5e1" style={{ marginBottom: 12 }} />
    <p style={{ fontSize: 14, margin: 0, fontWeight: 500 }}>{text}</p>
  </div>
);

const DAYS_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SLOT_COLORS: Record<string, { bg: string; fg: string; border: string }> = {
  morning:   { bg: "#fefce8", fg: "#854d0e", border: "#fde047" },
  afternoon: { bg: "#fff7ed", fg: "#9a3412", border: "#fb923c" },
  evening:   { bg: "#eff6ff", fg: "#1d4ed8", border: "#93c5fd" },
  custom:    { bg: "#f5f3ff", fg: "#5b21b6", border: "#c4b5fd" },
};

const fmt12 = (t24: string) => {
  if (!t24) return "";
  const [h, m] = t24.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m.toString().padStart(2, "0")} ${ampm}`;
};

const parseClientWeeklyPattern = (raw: any) => {
  const DAYS_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const pattern: Record<string, any> = {};
  if (!raw) return pattern;

  let rawDays = raw?.schedule?.days || raw?.days || (Array.isArray(raw) ? raw : null);
  const dayMap: Record<string, any> = {};

  if (Array.isArray(rawDays)) {
    rawDays.forEach((d: any) => { if (d?.day) dayMap[d.day] = d; });
  } else if (rawDays && typeof rawDays === "object") {
    Object.entries(rawDays).forEach(([k, v]) => { dayMap[k] = v; });
  }

  DAYS_ORDER.forEach(day => {
    const cfg = dayMap[day];
    if (!cfg || cfg.isWorking === false || cfg.active === false) {
      pattern[day] = { active: false, timeSlots: {} };
      return;
    }
    const ts = cfg.timeSlots || cfg;
    const morning = ts.morning?.start && ts.morning?.end ? ts.morning : (typeof ts.morning === "boolean" && ts.morning ? { start: "09:00", end: "12:00" } : (ts.morning?.from && ts.morning?.to ? { start: ts.morning.from, end: ts.morning.to } : null));
    const afternoon = ts.afternoon?.start && ts.afternoon?.end ? ts.afternoon : (typeof ts.afternoon === "boolean" && ts.afternoon ? { start: "12:00", end: "17:00" } : (ts.afternoon?.from && ts.afternoon?.to ? { start: ts.afternoon.from, end: ts.afternoon.to } : null));
    const evening = ts.evening?.start && ts.evening?.end ? ts.evening : (typeof ts.evening === "boolean" && ts.evening ? { start: "17:00", end: "21:00" } : (ts.evening?.from && ts.evening?.to ? { start: ts.evening.from, end: ts.evening.to } : null));
    const custom = Array.isArray(ts.custom) ? ts.custom : (Array.isArray(cfg.slots) ? cfg.slots : []);

    const hasSlots = Boolean(morning || afternoon || evening || (custom && custom.length > 0));
    pattern[day] = {
      active: hasSlots,
      timeSlots: { morning, afternoon, evening, custom }
    };
  });
  return pattern;
};

const ScheduleTab = ({ data }: { data: any }) => {
  const [schedDetails, setSchedDetails] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [daysLookahead, setDaysLookahead] = useState<number>(14);

  useEffect(() => {
    fetchScheduleData(daysLookahead);
  }, [data?.id, daysLookahead]);

  const fetchScheduleData = async (days: number) => {
    if (!data?.id) return;
    setLoading(true);
    try {
      const res = await getScholarScheduleDetails(data.id, days);
      if (res) {
        setSchedDetails(res);
      }
    } catch (err) {
      console.error("Failed to load schedule details:", err);
    } finally {
      setLoading(false);
    }
  };

  const isAvailable = schedDetails?.isAvailable ?? data?.isAvailable ?? true;
  const primaryBase = schedDetails?.primaryBase || {
    address: [data?.address, data?.city, data?.state].filter(Boolean).join(", ") || "Primary Base",
    city: data?.city,
    state: data?.state,
    latitude: data?.latitude,
    longitude: data?.longitude,
    serviceRadiusKm: data?.serviceRadius || 15
  };
  const coverageAreas: any[] = schedDetails?.coverageAreas || data?.availabilitySchedule?.coverageAreas || [];
  const metrics = schedDetails?.metrics || {
    totalOpenSlots: 0,
    totalBookedSlots: 0,
    totalAvailableSlots: 0,
    occupancyRate: 0,
    totalSlotRevenue: 0
  };
  const weeklyPattern = schedDetails?.weeklyPattern || parseClientWeeklyPattern(data?.availabilitySchedule);
  const dailySlotMatrix: any[] = schedDetails?.dailySlotMatrix || [];

  return (
    <div>
      {/* 1. LIVE ONLINE/OFFLINE STATUS & PRIMARY REACH */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
        <div style={{
          flex: 1, minWidth: 260, padding: "18px 22px", borderRadius: 16,
          background: isAvailable ? "#f0fdf4" : "#fef2f2",
          border: `1.5px solid ${isAvailable ? "#bbf7d0" : "#fecaca"}`
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 14, height: 14, borderRadius: "50%",
              background: isAvailable ? "#22c55e" : "#ef4444",
              boxShadow: isAvailable ? "0 0 0 4px rgba(34,197,94,0.2)" : "0 0 0 4px rgba(239,68,68,0.2)"
            }} />
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: isAvailable ? "#15803d" : "#b91c1c" }}>
                {isAvailable ? "Scholar Currently ONLINE" : "Scholar Currently OFFLINE"}
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                {isAvailable ? "Available to accept real-time bookings" : "Bookings paused / scholar unavailable"}
              </div>
            </div>
          </div>
        </div>

        <div style={{
          flex: 1, minWidth: 320, padding: "18px 22px", borderRadius: 16,
          background: "#f8fafc", border: "1.5px solid #e2e8f0"
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#2563eb", fontWeight: 800, fontSize: 13 }}>
              <Navigation size={16} /> Primary Focal Point & Work Base
            </div>
            {primaryBase.latitude && primaryBase.longitude && (
              <a
                href={`https://www.google.com/maps?q=${primaryBase.latitude},${primaryBase.longitude}`}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  fontSize: 11, fontWeight: 700, color: "#2563eb",
                  textDecoration: "none", background: "#eff6ff",
                  padding: "3px 8px", borderRadius: 8, border: "1px solid #bfdbfe"
                }}
              >
                <ExternalLink size={12} /> View Map
              </a>
            )}
          </div>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b", marginBottom: 6 }}>
            {primaryBase.address || [data?.address, data?.city, data?.state].filter(Boolean).join(", ") || "Focal Point Address"}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, fontSize: 12, color: "#64748b" }}>
            <div style={{ background: "#f1f5f9", padding: "3px 8px", borderRadius: 6, display: "flex", alignItems: "center", gap: 4 }}>
              <MapPin size={12} color="#475569" />
              <span>Focal GPS: <b>{primaryBase.latitude && primaryBase.longitude ? `${Number(primaryBase.latitude).toFixed(4)}°, ${Number(primaryBase.longitude).toFixed(4)}°` : "Resolving GPS..."}</b></span>
            </div>
            <div style={{ background: "#f1f5f9", padding: "3px 8px", borderRadius: 6 }}>
              Radial Reach: <b>{primaryBase.serviceRadiusKm} km circle</b>
            </div>
          </div>
        </div>
      </div>

      {/* 2. EXTENDED COVERAGE CLUSTERS / ADD-ON SERVICE AREAS */}
      <div style={{ marginBottom: 28 }}>
        <h4 style={{ fontSize: 13, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <MapPin size={16} color="#7c3aed" /> Add-On Coverage Areas & Cluster Zones ({coverageAreas.length})
        </h4>
        {coverageAreas.length === 0 ? (
          <div style={{ padding: "16px 20px", borderRadius: 12, border: "1px dashed #cbd5e1", background: "#f8fafc", fontSize: 13, color: "#94a3b8" }}>
            No secondary coverage area clusters added by scholar.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
            {coverageAreas.map((area: any) => (
              <div key={area.id || area.name} style={{
                padding: "14px 18px", borderRadius: 14,
                border: area.active !== false ? "1px solid #c4b5fd" : "1px solid #e2e8f0",
                background: area.active !== false ? "#f5f3ff" : "#f8fafc",
                display: "flex", alignItems: "center", gap: 12
              }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: area.active !== false ? "#8b5cf6" : "#cbd5e1", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flexShrink: 0 }}>
                  <MapPin size={18} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {area.name}
                  </div>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                    Cluster Coverage: <b>{area.radiusKm || 10} km</b>
                  </div>
                  {area.latitude && area.longitude && (
                    <div style={{ fontSize: 10, fontFamily: "monospace", color: "#94a3b8", marginTop: 1 }}>
                      {Number(area.latitude).toFixed(4)}, {Number(area.longitude).toFixed(4)}
                    </div>
                  )}
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 12,
                  background: area.active !== false ? "#dcfce7" : "#fee2e2",
                  color: area.active !== false ? "#15803d" : "#b91c1c"
                }}>
                  {area.active !== false ? "ACTIVE" : "PAUSED"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 3. MICRO-SLOT ANALYTICS CARDS */}
      <h4 style={{ fontSize: 13, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
        <TrendingUp size={16} color="#2563eb" /> Slot Performance & Occupancy ({daysLookahead} Days Forecast)
      </h4>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 28 }}>
        <div style={statCard}>
          <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Total Open Slots</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#1e293b", marginTop: 4 }}>{metrics.totalOpenSlots}</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Published time slots</div>
        </div>

        <div style={statCard}>
          <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Booked Slots</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#2563eb", marginTop: 4 }}>{metrics.totalBookedSlots}</div>
          <div style={{ fontSize: 12, color: "#10b981", fontWeight: 600, marginTop: 2 }}>Reserved by customers</div>
        </div>

        <div style={statCard}>
          <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Available Slots</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#10b981", marginTop: 4 }}>{metrics.totalAvailableSlots}</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Open for booking</div>
        </div>

        <div style={{ ...statCard, border: "1px solid #bfdbfe", background: "#eff6ff" }}>
          <div style={{ fontSize: 11, color: "#1d4ed8", fontWeight: 700, textTransform: "uppercase" }}>Occupancy Rate</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#1d4ed8", marginTop: 4 }}>{metrics.occupancyRate}%</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Slot conversion efficiency</div>
        </div>

        <div style={statCard}>
          <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Slot Revenue</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#7c3aed", marginTop: 4 }}>₹{metrics.totalSlotRevenue.toLocaleString("en-IN")}</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Bookings value</div>
        </div>
      </div>

      {/* 4. WEEKLY RECURRING SCHEDULE & DAY OFF TRACKING */}
      <h4 style={{ fontSize: 13, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 14 }}>
        Weekly Recurring Schedule & Days Off
      </h4>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 32 }}>
        {DAYS_ORDER.map(day => {
          const dayInfo = weeklyPattern[day] || {};
          const hasSlots = dayInfo.active;
          const slots = dayInfo.timeSlots || {};

          return (
            <div key={day} style={{
              display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 16px",
              borderRadius: 12, border: "1px solid #e2e8f0",
              background: hasSlots ? "#fff" : "#fef2f2",
              opacity: hasSlots ? 1 : 0.75
            }}>
              <div style={{ width: 110, flexShrink: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: hasSlots ? "#1e293b" : "#b91c1c" }}>{day}</div>
                <div style={{ fontSize: 11, color: hasSlots ? "#10b981" : "#ef4444", fontWeight: 600, marginTop: 2 }}>
                  {hasSlots ? "✓ Active" : "✕ Disabled / Off"}
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, flex: 1 }}>
                {hasSlots ? (
                  <>
                    {slots.morning && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, fontSize: 12, background: SLOT_COLORS.morning.bg, color: SLOT_COLORS.morning.fg, border: `1px solid ${SLOT_COLORS.morning.border}`, fontWeight: 600 }}>
                        <Sunrise size={13} /> Morning • {fmt12(slots.morning.start)} – {fmt12(slots.morning.end)}
                      </div>
                    )}
                    {slots.afternoon && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, fontSize: 12, background: SLOT_COLORS.afternoon.bg, color: SLOT_COLORS.afternoon.fg, border: `1px solid ${SLOT_COLORS.afternoon.border}`, fontWeight: 600 }}>
                        <Sun size={13} /> Afternoon • {fmt12(slots.afternoon.start)} – {fmt12(slots.afternoon.end)}
                      </div>
                    )}
                    {slots.evening && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, fontSize: 12, background: SLOT_COLORS.evening.bg, color: SLOT_COLORS.evening.fg, border: `1px solid ${SLOT_COLORS.evening.border}`, fontWeight: 600 }}>
                        <Moon size={13} /> Evening • {fmt12(slots.evening.start)} – {fmt12(slots.evening.end)}
                      </div>
                    )}
                    {(slots.custom || []).map((cs: any, i: number) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, fontSize: 12, background: SLOT_COLORS.custom.bg, color: SLOT_COLORS.custom.fg, border: `1px solid ${SLOT_COLORS.custom.border}`, fontWeight: 600 }}>
                        <Clock size={13} /> Custom • {fmt12(cs.start)} – {fmt12(cs.end)}
                      </div>
                    ))}
                  </>
                ) : <span style={{ fontSize: 12, color: "#b91c1c", fontStyle: "italic", fontWeight: 600 }}>Day Disabled by Scholar (No slots available)</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* 5. UPCOMING DAILY SLOT MATRIX */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h4 style={{ fontSize: 13, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5, margin: 0 }}>
          Upcoming Daily Slot Matrix
        </h4>
        <div style={{ display: "flex", gap: 6 }}>
          {[7, 14, 30].map(d => (
            <button key={d} onClick={() => setDaysLookahead(d)} style={{
              padding: "4px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700,
              border: daysLookahead === d ? "1px solid #2563eb" : "1px solid #e2e8f0",
              background: daysLookahead === d ? "#2563eb" : "#fff",
              color: daysLookahead === d ? "#fff" : "#64748b",
              cursor: "pointer"
            }}>
              {d} Days
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "#94a3b8" }}>
          <Clock size={28} style={{ animation: "spin 2s linear infinite", marginBottom: 8 }} />
          <p style={{ fontSize: 13, fontWeight: 600 }}>Loading micro slot matrix...</p>
        </div>
      ) : dailySlotMatrix.length === 0 ? (
        <EmptyState icon={Calendar} text="No slot schedule matrix found for selected period." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {dailySlotMatrix.map((dayData: any) => (
            <div key={dayData.date} style={{
              border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden", background: "#fff"
            }}>
              <div style={{
                padding: "10px 16px", background: dayData.active ? "#f8fafc" : "#fef2f2",
                borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center"
              }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: dayData.active ? "#1e293b" : "#b91c1c" }}>
                  {new Date(dayData.date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                  background: dayData.active ? "#dcfce7" : "#fee2e2",
                  color: dayData.active ? "#15803d" : "#b91c1c"
                }}>
                  {dayData.active ? `${dayData.slots.length} Slots Published` : "DAY OFF / DISABLED"}
                </span>
              </div>

              <div style={{ padding: 14 }}>
                {!dayData.active || dayData.slots.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>No slots configured for this day.</div>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {dayData.slots.map((slot: any) => {
                      const isBooked = slot.status === "BOOKED";
                      return (
                        <div key={slot.id} style={{
                          padding: "10px 14px", borderRadius: 12,
                          border: isBooked ? "1px solid #86efac" : "1px solid #e2e8f0",
                          background: isBooked ? "#f0fdf4" : "#f8fafc",
                          flex: "1 1 200px", maxWidth: 280
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>
                              {slot.type} • {fmt12(slot.startTime)} – {fmt12(slot.endTime)}
                            </span>
                            <Badge status={slot.status} />
                          </div>
                          {isBooked && slot.booking && (
                            <div style={{ fontSize: 11, color: "#15803d", marginTop: 4, paddingTop: 4, borderTop: "1px dashed #bbf7d0" }}>
                              <div><b>Customer:</b> {slot.booking.customerName} ({slot.booking.customerPhone})</div>
                              <div><b>Service:</b> {slot.booking.serviceName} • <b>₹{slot.booking.price}</b></div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProfileModal;
